import React, { useState, useEffect, useRef } from 'react';
import './KebabMenu.css';

// Mobile-only 3-dot vertical menu, top-right of the game screen.
// Items:
//   Move History   → opens a full-screen overlay of the move list
//   Export CSV     → downloads the game as CSV (existing GameHistory logic)
//   Export Position→ downloads the current board as an ASCII text file
//   Save & Exit    → returns to menu (game persists on the server)
//   Resign         → forfeit the game (only shown while game is active)
//
// The callbacks are the same ones GameHistory used to have — we just wire
// them to menu items instead of buttons inside the collapsed panel.
export default function KebabMenu({
  onExportCsv,
  onExportPosition,
  onSaveAndExit,
  onResign,
  onOpenMoveHistory,
  canSave,
  canResign,
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Dismiss on outside-tap.
  useEffect(() => {
    if (!open) return;
    const onTap = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onTap);
    document.addEventListener('touchstart', onTap);
    return () => {
      document.removeEventListener('mousedown', onTap);
      document.removeEventListener('touchstart', onTap);
    };
  }, [open]);

  const pick = (cb) => {
    setOpen(false);
    if (typeof cb === 'function') cb();
  };

  return (
    <div className="kebab-wrap" ref={wrapRef}>
      <button
        type="button"
        className="kebab-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-label="Open game actions"
        aria-expanded={open}
      >
        <span aria-hidden="true">⋮</span>
      </button>

      {open && (
        <div className="kebab-menu" role="menu">
          <button
            type="button"
            role="menuitem"
            className="kebab-item"
            onClick={() => pick(onOpenMoveHistory)}
          >
            <span className="kebab-icon" aria-hidden="true">📜</span>
            Move History
          </button>
          <button
            type="button"
            role="menuitem"
            className="kebab-item"
            onClick={() => pick(onExportCsv)}
          >
            <span className="kebab-icon" aria-hidden="true">📥</span>
            Export CSV
          </button>
          <button
            type="button"
            role="menuitem"
            className="kebab-item"
            onClick={() => pick(onExportPosition)}
          >
            <span className="kebab-icon" aria-hidden="true">📄</span>
            Export Position
          </button>
          {canSave && (
            <button
              type="button"
              role="menuitem"
              className="kebab-item"
              onClick={() => pick(onSaveAndExit)}
            >
              <span className="kebab-icon" aria-hidden="true">💾</span>
              Save &amp; Exit
            </button>
          )}
          {canResign && (
            <button
              type="button"
              role="menuitem"
              className="kebab-item kebab-item-danger"
              onClick={() => pick(onResign)}
            >
              <span className="kebab-icon" aria-hidden="true">🏳️</span>
              Resign
            </button>
          )}
        </div>
      )}
    </div>
  );
}
